import axios from "axios";

/**
 * Pre-configured Axios instance for the ChargeSuite API.
 *
 * - Base URL defaults to "/api" which Vite proxies to localhost:3001 in dev.
 * - A request interceptor automatically attaches the Clerk session JWT
 *   as a Bearer token for authenticated requests.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
});

/**
 * Global reference to store the getToken function from useAuth hook.
 * This is set by the app initialization and used by the interceptor.
 */
let getTokenFunction: (() => Promise<string | null>) | null = null;

/**
 * Set the token retrieval function. Call this from a component that has
 * access to Clerk's useAuth hook.
 */
export function setAuthTokenGetter(getToken: () => Promise<string | null>) {
  getTokenFunction = getToken;
}

/**
 * Attach Clerk auth token to every outgoing request.
 *
 * Uses the token getter function provided via setAuthTokenGetter.
 */
api.interceptors.request.use(async (config) => {
  try {
    if (getTokenFunction) {
      const token = await getTokenFunction();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {
    // Silently continue without auth token - the API will return 401
    // and the UI can handle redirection via Clerk's SignedOut component.
  }

  return config;
});

/**
 * Global response error handler.
 * Specific error handling (toasts, redirects) should be added per-feature.
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      if (status === 401) {
        // Session expired or invalid - Clerk's SignedOut component
        // will handle the redirect automatically.
        console.warn("[api] Unauthorized request - session may have expired.");
      }

      if (status === 429) {
        console.warn("[api] Rate limited. Retrying is left to the caller.");
      }
    }

    return Promise.reject(error);
  },
);

export { api };
